import type { CSSProperties } from 'react';
import { DISCOUNT_LABELS, type DiscountChoice, type DiscountType } from '../lib/discounts';

interface Service {
  booking_service_id?: number | string;
  name: string;
  price: number | string;
}

const sectionLabel: CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'var(--color-text-muted)',
  marginBottom: '8px',
};

/**
 * Loyalty card / Voucher Discount picker for the booking panel at checkout.
 * Tapping the active option again removes the discount. The two never
 * stack. With several services the barber picks which one the loyalty card
 * makes free.
 */
export function CheckoutDiscount({
  services,
  choice,
  discountCents,
  onChange,
}: {
  services: Service[];
  choice: DiscountChoice | null;
  discountCents: number;
  onChange: (choice: DiscountChoice | null) => void;
}) {
  const hasServices = services.length > 0;

  const toggle = (type: DiscountType) => {
    if (choice?.type === type) return onChange(null);
    if (type === 'loyalty') {
      return onChange({ type, bookingServiceId: Number(services[0]?.booking_service_id) });
    }
    onChange({ type });
  };

  const option = (type: DiscountType, label: string, disabled = false) => {
    const active = choice?.type === type;
    return (
      <button
        type="button"
        aria-pressed={active}
        disabled={disabled}
        onClick={() => toggle(type)}
        style={{
          flex: 1,
          minHeight: '44px',
          padding: '8px 10px',
          borderRadius: '8px',
          border: active ? '1px solid rgba(217,178,86,0.6)' : '1px solid var(--color-border)',
          backgroundColor: active ? 'rgba(217,178,86,0.14)' : '#fff',
          color: disabled ? 'var(--color-text-muted)' : 'var(--color-text)',
          fontWeight: active ? 700 : 500,
          fontSize: '13px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        {active ? '✓ ' : ''}
        {label}
      </button>
    );
  };

  return (
    <div style={{ marginBottom: '16px' }}>
      <div style={sectionLabel}>Discount</div>
      <div style={{ display: 'flex', gap: '8px' }}>
        {option('loyalty', '🎟 Loyalty card', !hasServices)}
        {option('voucher', 'Voucher Discount')}
      </div>

      {choice?.type === 'loyalty' && services.length > 1 && (
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginTop: '8px',
            fontSize: '13px',
            color: 'var(--color-text-muted)',
          }}
        >
          Free service
          <select
            value={String(choice.bookingServiceId ?? '')}
            onChange={(e) =>
              onChange({ type: 'loyalty', bookingServiceId: Number(e.target.value) })
            }
            style={{
              flex: 1,
              minHeight: '44px',
              padding: '6px 10px',
              fontSize: '14px',
              borderRadius: '8px',
              border: '1px solid var(--color-border)',
              backgroundColor: '#fff',
              color: 'var(--color-text)',
            }}
          >
            {services.map((s) => (
              <option key={String(s.booking_service_id)} value={String(s.booking_service_id)}>
                {s.name} · ${Number(s.price).toFixed(2)}
              </option>
            ))}
          </select>
        </label>
      )}

      {choice && discountCents > 0 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '8px',
            padding: '8px 14px',
            fontSize: '13px',
            borderRadius: '8px',
            backgroundColor: 'rgba(40,167,69,0.08)',
            color: '#1e7e34',
            fontWeight: 600,
          }}
        >
          <span>{DISCOUNT_LABELS[choice.type]}</span>
          <span>−${(discountCents / 100).toFixed(2)}</span>
        </div>
      )}
    </div>
  );
}
